import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import {
  TunerProfile,
  TunerPublic,
  TunerClient,
  TunerWallet,
  TunerTransaction,
  UserTuner,
  BecomeTunerResponse,
  AffiliateResponse,
} from '../model/response/tuner-response';
import { DowngradePreviewResponse, DowngradeConfirmResponse } from '../model/response/downgrade-preview-response';

@Injectable({ providedIn: 'root' })
export class TunerService {
  private baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = enviroment.BASE_URL_API;
  }

  becomeTuner(userId: string): Observable<BecomeTunerResponse> {
    return this.http.post<BecomeTunerResponse>(`${this.baseUrl}/user/${userId}/become-tuner`, {});
  }

  /**
   * Lista tuners disponíveis. Quando planId é informado, a API filtra também
   * pelo tier aceito — o front não decide mais isso (a regra vive só no backend).
   */
  getPublicTuners(planId?: string): Observable<TunerPublic[]> {
    const url = planId
      ? `${this.baseUrl}/tuners?planId=${encodeURIComponent(planId)}`
      : `${this.baseUrl}/tuners`;
    return this.http.get<TunerPublic[]>(url);
  }

  /**
   * Valida um código de convite. Com planId, a API devolve 400 se o tuner não
   * aceitar o tier do plano escolhido — o usuário descobre antes de pagar.
   */
  validateInviteCode(inviteCode: string, planId?: string): Observable<TunerPublic> {
    const url = planId
      ? `${this.baseUrl}/tuner/invite/${inviteCode}?planId=${encodeURIComponent(planId)}`
      : `${this.baseUrl}/tuner/invite/${inviteCode}`;
    return this.http.get<TunerPublic>(url);
  }

  affiliateToTuner(userId: string, body: { tunerId?: string | null; inviteCode?: string | null }): Observable<AffiliateResponse> {
    return this.http.post<AffiliateResponse>(`${this.baseUrl}/user/${userId}/tuner`, body);
  }

  getUserTuner(userId: string): Observable<UserTuner> {
    return this.http.get<UserTuner>(`${this.baseUrl}/user/${userId}/tuner`);
  }

  removeAffiliation(userId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/user/${userId}/tuner`);
  }

  getTunerProfile(tunerId: string): Observable<TunerProfile> {
    return this.http.get<TunerProfile>(`${this.baseUrl}/tuner/${tunerId}/profile`);
  }

  updateTunerProfile(tunerId: string, body: Partial<TunerProfile>): Observable<TunerProfile> {
    return this.http.put<TunerProfile>(`${this.baseUrl}/tuner/${tunerId}/profile`, body);
  }

  regenerateInviteCode(tunerId: string): Observable<{ inviteCode: string }> {
    return this.http.post<{ inviteCode: string }>(`${this.baseUrl}/tuner/${tunerId}/invite-link/regenerate`, {});
  }

  getTunerClients(tunerId: string): Observable<TunerClient[]> {
    return this.http.get<TunerClient[]>(`${this.baseUrl}/tuner/${tunerId}/clients`);
  }

  acceptClient(tunerId: string, userId: string): Observable<{ id: string; status: string }> {
    return this.http.post<{ id: string; status: string }>(`${this.baseUrl}/tuner/${tunerId}/clients/${userId}/accept`, {});
  }

  rejectClient(tunerId: string, userId: string): Observable<{ id: string; status: string }> {
    return this.http.post<{ id: string; status: string }>(`${this.baseUrl}/tuner/${tunerId}/clients/${userId}/reject`, {});
  }

  removeClient(tunerId: string, userId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/tuner/${tunerId}/clients/${userId}`);
  }

  getWallet(tunerId: string): Observable<TunerWallet> {
    return this.http.get<TunerWallet>(`${this.baseUrl}/tuner/${tunerId}/wallet`);
  }

  getTransactions(tunerId: string, page: number = 1, pageSize: number = 20): Observable<TunerTransaction[]> {
    return this.http.get<TunerTransaction[]>(`${this.baseUrl}/tuner/${tunerId}/wallet/transactions?page=${page}&pageSize=${pageSize}`);
  }

  getDowngradePreview(tunerId: string, planId: string): Observable<DowngradePreviewResponse> {
    return this.http.get<DowngradePreviewResponse>(
      `${this.baseUrl}/tuner/${tunerId}/downgrade-preview`, { params: { planId } });
  }

  confirmDowngrade(tunerId: string, targetPlanId: string, keepUserIds: string[]): Observable<DowngradeConfirmResponse> {
    return this.http.post<DowngradeConfirmResponse>(
      `${this.baseUrl}/tuner/${tunerId}/downgrade-preview/confirm`,
      { targetPlanId, keepUserIds });
  }
}
